import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";

import { ReactComponent as PinIcon } from "../../../media/icons/pin.svg";
import { ReactComponent as CalendarIcon } from "../../../media/icons/calendar.svg";
import { ReactComponent as TicketIcon } from "../../../media/icons/simpleTicket.svg";
import { ReactComponent as ShareIcon } from "../../../media/icons/share.svg";
import { Button } from "../../common/button";
import { Preloader } from "../../common/preloader";
import { ReactComponent as SaveIcon } from "../../../media/icons/heart.svg";
import defaultImage from "../../../media/images/defaultImage.png";

import styles from "./eventsListing.module.css";

const EventsListing = ({ list, isExpand, addToFavorite, removeFavorite, isOrganizations }) => {
  return (
    <div className={cn(styles.flex, isExpand ? styles.flexExpand : "")}>
      {list.map(item => {
        return (
          <Event
            isOrganizations={isOrganizations}
            key={`${item.title}-${item.added}`}
            item={item}
            isExpand={isExpand}
            addToFavorite={addToFavorite}
            removeFavorite={removeFavorite}
          />
        );
      })}
    </div>
  );
};

const Event = ({ isExpand, item, addToFavorite, removeFavorite, isOrganizations }) => {
  const [loading, setLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(item.bookmarked === "true");

  const toggleFavorite = async listingId => {
    if (loading) return;

    setLoading(true);
    try {
      if (isFavorite) {
        const res = await removeFavorite(listingId);
        if (res) {
          setIsFavorite(false);
        }
      } else {
        const res = await addToFavorite(listingId);
        if (res) {
          setIsFavorite(true);
        }
      }
    } catch (error) {
      console.error("Favorite toggle error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (item.bookmarked === "true") {
      setIsFavorite(true);
    }
  }, [item.bookmarked]);

  const location = useMemo(() => {
    if (item.lawfirms?.length) {
      const { state, city, openNow } = item.lawfirms[0];
      return { address: `${city}, ${state}`, openNow };
    }

    return "";
  }, [item.lawfirms?.length]);

  const ticket = useMemo(() => {
    if (item.event_tickets?.length) {
      return item.event_tickets[0];
    }

    return {};
  }, [item.lawfirms?.length]);

  const pathname = isOrganizations ? `/public-organizations/${item.listing_id}` : `/public-listing/${item.listing_id}`;

  return (
    <div className={cn(styles.tile, isExpand ? styles.tileExpand : "")} key={item.title}>
      <div className={styles.favorites}>
        <Button
          className={cn(styles.favoritesButton, isFavorite ? styles.favoritesButtonActive : "")}
          onClick={() => toggleFavorite(item.listing_id)}
          disabled={loading}
        >
          {loading ? <Preloader className={styles.spinner} /> : <SaveIcon />}
        </Button>
      </div>

      <div className={styles.share}>
        <Button className={styles.shareButton}>
          <ShareIcon />
        </Button>
      </div>

      <div className={isExpand ? styles.expandContainer : ""}>
        <Link to={pathname} className={styles.tileImage}>
          <img src={item.cover_image_url || defaultImage} alt="Events banner" />
        </Link>
        <div className={styles.titleContent}>
          <Link to={pathname} className={styles.tileTitle}>
            {item.attorney}
          </Link>
          <div className={styles.titleFlex}>
            {location.openNow ? (
              <span className={styles.titleDetail}>
                <CalendarIcon />
                {location.openNow}
              </span>
            ) : null}
            <span className={styles.titleDetail}>
              <PinIcon /> {location.address}
            </span>

            {ticket.currency || ticket.price ? (
              <span className={styles.titleDetail}>
                <TicketIcon /> {`${ticket.currency || ""} ${ticket.price || ""}`}
              </span>
            ) : null}
          </div>
          <p className={styles.tileDescription}>{item.description}</p>
        </div>
      </div>
    </div>
  );
};

export default EventsListing;
